import type { BotOption } from '@pypestream/testview/browser';

import type { ValidateTurn } from './validate-runs';

/**
 * A suite case's script: the testing tool's direct and mixed modes
 * (`apps/tester` `simple-test-runner.ts`), one step per line.
 *
 *   Hello                      — the customer types this
 *   click:Track my order       — taps a button the bot offered
 *   menu:Talk to a person      — picks from the persistent menu
 *   ai:Get a refund            — the simulated customer pursues this goal
 *   expect:order number        — the last reply must say this
 *   webview:submit {"a":1}     — the open webview hands over data, then closes
 *   webview:close              — closes the open webview
 *
 * Blank lines and lines starting with `#` are skipped. Pure, so the runner
 * and the editor's preview share one reading of a script.
 */

export type ScriptStep =
  | { kind: 'say'; text: string }
  | { kind: 'click'; label: string }
  | { kind: 'menu'; label: string }
  | { kind: 'ai'; goal: string }
  | { kind: 'expect'; text: string }
  | { kind: 'webview-close' }
  | { kind: 'webview-submit'; data: Record<string, unknown> | unknown[] };

export interface ParsedScript {
  steps: ScriptStep[];
  /** "Line 3: …" — a line that can't run as written. */
  errors: string[];
}

const PREFIX = /^(click|button|menu|ai|expect|webview)\s*:\s*(.*)$/i;

export const parseScript = (script: string): ParsedScript => {
  const steps: ScriptStep[] = [];
  const errors: string[] = [];
  script.split(/\r?\n/).forEach((raw, i) => {
    const line = raw.trim();
    if (!line || line.startsWith('#')) return;
    const m = PREFIX.exec(line);
    if (!m) {
      steps.push({ kind: 'say', text: line });
      return;
    }
    const kind = m[1].toLowerCase();
    const rest = m[2].trim();
    const fail = (why: string) => errors.push(`Line ${i + 1}: ${why}`);
    if (kind === 'webview') {
      const [verb, ...more] = rest.split(/\s+/);
      if (/^close$/i.test(verb ?? '')) {
        steps.push({ kind: 'webview-close' });
      } else if (/^submit$/i.test(verb ?? '')) {
        const json = more.join(' ').trim();
        try {
          const data: unknown = json ? JSON.parse(json) : {};
          if (!data || typeof data !== 'object')
            throw new Error('not an object');
          steps.push({
            kind: 'webview-submit',
            data: data as Record<string, unknown> | unknown[],
          });
        } catch {
          fail(
            'webview:submit takes JSON, e.g. webview:submit {"time":"13:30"}'
          );
        }
      } else {
        fail('use webview:close or webview:submit {…}');
      }
      return;
    }
    if (!rest) {
      fail(`${kind}: needs something after the colon`);
      return;
    }
    if (kind === 'click' || kind === 'button')
      steps.push({ kind: 'click', label: rest });
    else if (kind === 'menu') steps.push({ kind: 'menu', label: rest });
    else if (kind === 'ai') steps.push({ kind: 'ai', goal: rest });
    else steps.push({ kind: 'expect', text: rest });
  });
  return { steps, errors };
};

/** A step as the customer's side of the transcript shows it. */
export const describeStep = (step: ScriptStep): string => {
  switch (step.kind) {
    case 'say':
      return step.text;
    case 'click':
      return `Tap “${step.label}”`;
    case 'menu':
      return `Menu: “${step.label}”`;
    case 'ai':
      return `AI customer: ${step.goal}`;
    case 'expect':
      return `Check the reply says “${step.text}”`;
    case 'webview-close':
      return 'Close the webview';
    default:
      return 'Submit the webview';
  }
};

/* ---- Matching a button by its label -------------------------------------- */

/** "<<fas fa-home>> Main menu" / "🏠 Main menu!" → "main menu". */
export const normalizeLabel = (label: string): string =>
  label
    .replace(/<<[^>]*>>/g, ' ')
    .replace(/[^\p{L}\p{N}\s]/gu, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();

/** The option whose label matches: exactly, then as a prefix, then contained. */
export const findOption = (
  options: BotOption[] | undefined,
  label: string
): BotOption | undefined => {
  if (!options?.length) return undefined;
  const want = normalizeLabel(label);
  if (!want) return undefined;
  const norm = options.map((o) => ({ o, n: normalizeLabel(o.label) }));
  return (
    norm.find((x) => x.n === want)?.o ??
    norm.find((x) => x.n.startsWith(want))?.o ??
    norm.find((x) => x.n.includes(want))?.o
  );
};

/* ---- Checks -------------------------------------------------------------- */

export interface RunCheck {
  /** What was checked, as the run detail shows it. */
  label: string;
  passed: boolean;
}

/** `/order #\d+/i` is a regex; anything else matches as text, ignoring case. */
const matcher = (pattern: string): ((text: string) => boolean) => {
  const re = /^\/(.+)\/([a-z]*)$/i.exec(pattern.trim());
  if (re) {
    try {
      const rx = new RegExp(re[1], re[2].replace(/[gy]/g, ''));
      return (text) => rx.test(text);
    } catch {
      // Not a valid regex: treat it as text.
    }
  }
  const needle = pattern.trim().toLowerCase();
  return (text) => text.toLowerCase().includes(needle);
};

/** Does `text` say `pattern`? */
export const says = (text: string, pattern: string) => matcher(pattern)(text);

/** The bot's own words in a conversation, not the seeded or platform lines. */
const botWords = (transcript: ValidateTurn[]) =>
  transcript
    .filter((t) => t.role === 'bot' && !t.seeded && !t.system)
    .map((t) => t.text)
    .join('\n');

/**
 * The testing tool's success and failure patterns: every "must say" has to
 * appear somewhere in the bot's replies, and no "must not say" may.
 */
export const checkPatterns = (
  transcript: ValidateTurn[],
  {
    mustSay = [],
    mustNotSay = [],
  }: { mustSay?: string[]; mustNotSay?: string[] }
): RunCheck[] => {
  const words = botWords(transcript);
  return [
    ...mustSay
      .filter((p) => p.trim())
      .map((p) => ({ label: `Says “${p.trim()}”`, passed: says(words, p) })),
    ...mustNotSay
      .filter((p) => p.trim())
      .map((p) => ({
        label: `Never says “${p.trim()}”`,
        passed: !says(words, p),
      })),
  ];
};

/** One pattern per line, blanks dropped. */
export const patternLines = (text: string | undefined): string[] =>
  (text ?? '')
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean);
